/** @format */

import React, {
  type ChangeEvent,
  type FunctionComponent,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../util/classNameConstructor";
import Icon from "../../core/icon/Icon";
import Box from "../box/Box";
import Button from "../buttons/Button";
import CharacterCounter from "./CharacterCounter";
import type { InputProps } from "./Input.types";
import SelectGroup from "./SelectGroup";
import { InputContainer, InputField, Label } from "./input.styles";

const adjustTextarea = (event: Event, rows: number, maxRows?: number) => {
  if (!event?.target) return;
  const Element = event.target as HTMLTextAreaElement;
  Element.setAttribute("rows", "");
  const padding =
    Number.parseFloat(window.getComputedStyle(Element).paddingBlock) * 2;
  const lineHeight = Number.parseFloat(
    window.getComputedStyle(Element).lineHeight,
  );
  const scrollHeight = Element.scrollHeight - padding;
  const newRows = Math.round(scrollHeight / lineHeight);
  Element.setAttribute(
    "rows",
    Math.max(Math.min(newRows, maxRows || rows), rows).toString(),
  );
};

/**
 * @deprecated This component is deprecated and will be removed in future releases.
 * Please use the Input component from the core/inputs module
 */

const Input: FunctionComponent<
  InputProps & React.HTMLAttributes<HTMLDivElement>
> = ({
  // explicit props
  value,
  id,
  name = Math.random().toString(36).substring(2, 5),
  type,
  label,
  placeholder,
  note, // or helperText
  size, // sm, md, lg
  variant, // grey (default) or white
  autocomplete,

  leadingIcon, // leadingIcon
  trailingIcon, // trailingIcon
  leadingIconLabel,
  trailingIconLabel,

  required,
  disabled,
  clearable,

  rows = 3,
  maxRows = 10,
  // methods
  onChange,
  onFocus,
  onBlur,
  onClick,
  onSubmit,
  receiveValue,
  error,
  success,
  leadingIconClick,
  trailingIconClick,
  trailingIconCy,
  ref,

  autoFocus,
  minLength,
  maxLength,
  max,
  min,
  className,
  leadingLabel,

  // implicit props
  ...props
}) => {
  const { t } = useTranslation();
  const [count, setCount] = React.useState(0);
  const [isSearch, setIsSearch] = useState(type === "search");
  const [isPassword, setIsPassword] = useState(type === "password");
  const [isFocused, setIsFocused] = useState(false);
  // const [value, setValue] = useState("");
  const inputRef = ref || useRef(null);

  useEffect(() => {
    if (type === "textarea" && inputRef)
      adjustTextarea({ target: inputRef.current } as Event, rows, maxRows);
  }, [value, inputRef]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (minLength || maxLength) {
      setCount(event.target.value.length);
    }
    // if (type === "textarea") adjustTextarea(event, rows, maxRows);
    if (typeof receiveValue === "function") {
      receiveValue?.(event.target.value);
    }
    if (typeof onChange === "function") {
      // if (isSearch && event.target) return onChange(event.target.value)
      return onChange(event);
    }
    return null;
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (
      error ||
      event.key !== "Enter" ||
      (type &&
        type !== "password" &&
        type !== "email" &&
        type !== "mail" &&
        type !== "multiInputForm")
    )
      return;

    event.preventDefault();
    onSubmit?.(value);
  };

  const clear = clearable || isSearch;

  const placeholderState =
    placeholder || `${isSearch ? t("global:search") : ""}`;

  // const leadingIconState = leadingIcon;
  // const leadingIconClickState = leadingIconClick;
  // const trailingIconState = trailingIcon;
  // const trailingIconClickState = trailingIconClick;

  const [lengthError, setLengthError] = useState({
    error: false,
    activated: false,
  });
  const [leadingIconState, setLeadingIconState] = useState(leadingIcon);
  const [leadingIconClickState, setLeadingIconClickState] = useState(
    () => leadingIconClick,
  );
  const [leadingIconLabelState, setLeadingIconLabelState] =
    useState(leadingIconLabel);
  const [trailingIconState, setTrailingIconState] = useState(trailingIcon);
  const [trailingIconClickState, setTrailingIconClickState] = useState(
    () => trailingIconClick,
  );
  const [trailingIconLabelState, setTrailingIconLabelState] =
    useState(trailingIconLabel);

  useEffect(() => setLeadingIconState(leadingIcon), [leadingIcon]);
  useEffect(() => {
    if (!error) setLeadingIconClickState(() => leadingIconClick);
  }, [leadingIconClick, error]);
  useEffect(() => setTrailingIconState(trailingIcon), [trailingIcon]);
  useEffect(() => {
    setTrailingIconClickState(() => trailingIconClick);
  }, [trailingIconClick]);

  useEffect(() => {
    if (isSearch && !leadingIcon) {
      setLeadingIconState("search");
    }
  }, [isSearch, leadingIcon]);

  const resetTrailingIconState = () => {
    if (type === "password") {
      setTrailingIconState(isPassword ? "eye" : "eye-strikethrough");
      return;
    }
    if (clear && value) {
      setTrailingIconState("x-md");
      return;
    }
    setTrailingIconState(trailingIcon);
  };

  useEffect(() => {
    if (type === "password") {
      resetTrailingIconState();
      setTrailingIconClickState(() => () => setIsPassword(!isPassword));
    }
  }, [type, isPassword, error]);

  useEffect(() => {
    if (clear && value) {
      resetTrailingIconState();
      setTrailingIconClickState(() => () => {
        onChange?.({ target: { value: "" } } as ChangeEvent<HTMLInputElement>);
        setIsFocused((prevState) => !prevState);
      });
    }
    const lengthErrorValue =
      (minLength && (value?.toString()?.length || 0) < minLength) ||
      (maxLength && (value?.toString()?.length || 0) > maxLength);

    setLengthError((e) => {
      if (lengthErrorValue)
        return { ...e, error: e.activated && lengthErrorValue };
      return { error: false, activated: true };
    });
  }, [clear, value, error]);

  useEffect(() => {
    if (error) {
      setTrailingIconState("alert");
      setTimeout(() => {
        setTrailingIconState(null);
      }, 3000);
    }
  }, [error]);

  const LeadingIconWrapper =
    typeof leadingIconClickState === "function" ? Button : "label";
  const TrailingIconWrapper =
    typeof trailingIconClickState === "function" ? Button : "label";

  const iconWrapperProps = (onClick) =>
    onClick && typeof onClick === "function"
      ? {
          variant: "tertiary",
          size: "small",
          onClick,
        }
      : {
          htmlFor: id || name,
        };

  const fieldType =
    type === "textarea"
      ? undefined
      : !type || (type === "password" && !isPassword)
        ? "text"
        : type;

  const [inputFocused, setInputFocused] = React.useState(false);

  const iconSize = 16;
  // const iconSize = size === "small" || size === "sm" ? 14 : 16;
  const iconDimensions = { width: iconSize, height: iconSize };

  return (
    <div
      className={cn(
        "flex flex-col gap-3 w-full min-h-auto",
        disabled && "opacity-50",
        props.hidden && "hidden",
        className,
      )}
    >
      {label && typeof label === "string" ? (
        <Label
          error={error}
          active={isFocused && value !== ""}
          size={size}
        >{`${label}${required ? "*" : ""}`}</Label>
      ) : (
        label
      )}
      {/* the InputContainer wrapper is necessary for including icons and buttons */}
      {type === "radio" || type === "checkbox" ? (
        <Box
          margin="-10px"
          marginTop={label && "0px"}
          marginBottom={note && "0px"}
        >
          <SelectGroup
            type={type as "radio" | "checkbox"}
            value={value as string}
            onChange={receiveValue}
            name={name}
            {...props}
          />
        </Box>
      ) : (
        <InputContainer
          // focus={isFocused}
          icon={leadingIconState}
          onFocusCapture={() => setIsFocused((prevState) => !prevState)}
          onBlurCapture={() => setIsFocused((prevState) => !prevState)}
          onFocus={onFocus}
          onBlur={onBlur}
          size={size || "lg"}
          textarea={type === "textarea"}
          variant={variant}
          focused={inputFocused || isFocused}
          error={error || lengthError.error}
        >
          {/* {label && (
          <Label error={error} size={size} icon={!!leadingIconState} active={isFocused || value !== ""}>{`${label}${required ? "*" : ""}`}</Label>
        )} */}
          {leadingIconState && (
            <LeadingIconWrapper
              {...iconWrapperProps(leadingIconClickState)}
              title={leadingIconLabelState}
            >
              <Icon
                name={leadingIconState}
                className="text-icon-default"
                style={{ pointerEvents: "none" }}
                {...iconDimensions}
              />
            </LeadingIconWrapper>
          )}
          {leadingLabel ? (
            <span className="text-body-md -mr-2 text-text-subdued select-none">
              {leadingLabel}
            </span>
          ) : null}
          <InputField
            id={id || name}
            type={fieldType}
            placeholder={placeholderState}
            disabled={disabled}
            {...(type === "textarea" ? { rows: Math.max(rows, 2) } : {})}
            value={value ?? ""}
            onClick={(e) => {
              onClick?.(e);
              resetTrailingIconState();
            }}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            ref={inputRef}
            as={type === "textarea" ? "textarea" : "input"}
            autoComplete={autocomplete}
            onFocus={({ nativeEvent }) =>
              "sourceCapabilities" in nativeEvent &&
              nativeEvent?.sourceCapabilities
                ? setInputFocused(true)
                : null
            }
            min={min}
            max={max}
            onBlurCapture={() => setInputFocused(false)}
            autoFocus={autoFocus}
            aria-required={required ? "true" : "false"}
            aria-label={props["aria-label"]}
          />
          {trailingIconState && (
            <TrailingIconWrapper
              {...iconWrapperProps(trailingIconClickState)}
              title={trailingIconLabelState}
              data-cy={trailingIconCy}
            >
              <Icon
                name={trailingIconState}
                className={cn(
                  error ? "text-icon-critical" : "text-icon-default",
                )}
                style={{ pointerEvents: "none" }}
                {...iconDimensions}
              />
            </TrailingIconWrapper>
          )}
        </InputContainer>
      )}
      <CharacterCounter
        value={value}
        minLength={minLength}
        maxLength={maxLength}
        note={note}
        error={error}
        size={size}
      />
    </div>
  );
};

export default Input;
